import { useTranslation } from '@hbs-us/ui';
import { useNavigate } from '@tanstack/react-router';
import { useState } from 'react';

import {
  type NeedsEstimateVisit,
  useNeedsEstimateQuery,
  useStartEstimateFromVisit,
} from './useNeedsEstimateQuery.js';

/**
 * Site visits the crew has been out to that nobody has priced yet.
 *
 * Sits above the estimates list because this is where somebody sits down to write estimates. A
 * visit that came back and never became a bid is a truck, an hour and a technician already spent
 * for nothing, and before this there was nowhere that fact was visible — the crew's report existed
 * only on the dispatch board, if it existed at all.
 *
 * The panel renders NOTHING when the queue is empty. An always-present "0 waiting" block is
 * furniture; a block that only appears when there is work to do is a signal.
 *
 * The crew's words are shown here in full, deliberately. They are what the office prices from, and
 * they are internal — this text is never carried into the estimate's scope of work, which prints on
 * the document the customer reads.
 */
export function NeedsPricingPanel() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const query = useNeedsEstimateQuery();
  const startEstimate = useStartEstimateFromVisit();
  const [pending, setPending] = useState<number | null>(null);

  const visits = query.data?.visits ?? [];

  // Quiet on failure. This panel is an addition to the estimates page, and a queue that cannot load
  // must not take the estimates list down with it.
  if (query.isPending || query.isError || visits.length === 0) return null;

  const handleStart = (visit: NeedsEstimateVisit) => {
    setPending(visit.assignmentId);
    startEstimate.mutate(
      { assignmentId: visit.assignmentId },
      {
        onSuccess: (res) => {
          setPending(null);
          // Land on the estimate — the point of pressing this is to price it now. The crew's report
          // travels with it and is shown beside the form.
          if (res.estimateId) {
            void navigate({ to: '/estimates/$id', params: { id: String(res.estimateId) } });
          }
        },
        onError: () => setPending(null),
      },
    );
  };

  return (
    <section
      aria-labelledby="needs-pricing-heading"
      className="mb-6 rounded-lg border border-border bg-card p-4"
    >
      <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
        <h2
          id="needs-pricing-heading"
          className="font-display font-semibold text-foreground text-lg"
        >
          {t('estimates:needsPricing.heading')}
        </h2>
        <p className="text-muted-foreground text-sm">
          {t('estimates:needsPricing.count', { count: visits.length })}
        </p>
      </div>

      <ul className="flex flex-col gap-3">
        {visits.map((visit) => (
          <li
            key={visit.assignmentId}
            className="flex flex-col gap-2 rounded-md border border-border/60 bg-background p-3 sm:flex-row sm:items-start sm:justify-between"
          >
            <div className="min-w-0">
              <p className="font-medium text-foreground text-sm">
                {visit.customerName ?? visit.jobName ?? t('estimates:needsPricing.noCustomer')}
              </p>
              <p className="text-muted-foreground text-xs">
                {t('estimates:needsPricing.visitedBy', {
                  crew: visit.crewName,
                  date: visit.scheduledDate,
                })}
              </p>
              {visit.customerAddress ? (
                <p className="truncate text-muted-foreground text-xs">{visit.customerAddress}</p>
              ) : null}

              {/* The crew's report, in full. This is the thing being priced from. */}
              <p className="mt-2 whitespace-pre-line border-border border-l-2 pl-2 text-foreground text-sm">
                {visit.fieldNotes}
              </p>
            </div>

            <button
              type="button"
              onClick={() => handleStart(visit)}
              disabled={pending === visit.assignmentId}
              className="inline-flex h-9 shrink-0 items-center justify-center rounded-md bg-primary px-3 font-medium text-primary-foreground text-sm transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:opacity-60"
            >
              {pending === visit.assignmentId
                ? t('estimates:needsPricing.starting')
                : t('estimates:needsPricing.write')}
            </button>
          </li>
        ))}
      </ul>

      {startEstimate.isError ? (
        <p role="alert" className="mt-3 text-destructive text-sm">
          {t('estimates:needsPricing.failed')}
        </p>
      ) : null}
    </section>
  );
}
